// apps/web/src/pages/BillingSettingsPage.tsx
//
// Global billing settings: cron schedule, NIT block, approval flow.
import { useEffect, useState } from 'react';
import { Loader2, Save, Calendar, AlertTriangle, ShieldCheck, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getBillingCronConfig, setBillingCronConfig,
  getBillingFlags, setBillingFlag,
  getSuspensionPolicy, setSuspensionPolicy,
  type BillingCronConfig, type SuspensionPolicy,
} from '@/services/billing';

export function BillingSettingsPage() {
  const { hasRole } = usePermissions();
  const canManage = hasRole('admin') || hasRole('super_admin');

  const [cron, setCron]         = useState<BillingCronConfig | null>(null);
  const [flags, setFlags]       = useState({ block_unvalidated_nit: true, require_approval: true });
  const [policy, setPolicy]     = useState<SuspensionPolicy>({ overdue_count: 2, overdue_days: 60 });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [savedAt, setSavedAt]   = useState<Date | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, f, p] = await Promise.all([getBillingCronConfig(), getBillingFlags(), getSuspensionPolicy()]);
        setCron(c); setFlags(f); setPolicy(p);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudieron cargar las opciones');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save() {
    if (!cron) return;
    setSaving(true);
    setError(null);
    try {
      await setBillingCronConfig(cron);
      await setBillingFlag('billing_block_unvalidated_nit', flags.block_unvalidated_nit);
      await setBillingFlag('billing_require_approval', flags.require_approval);
      await setSuspensionPolicy(policy);
      setSavedAt(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  if (!canManage) {
    return (
      <div className="container mx-auto p-6">
        <Card><CardContent className="p-6 text-muted-foreground">
          Solo administradores pueden modificar la configuración de facturación.
        </CardContent></Card>
      </div>
    );
  }

  if (loading || !cron) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" /> Configuración de facturación
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Programación, aprobación y bloqueos del sistema de facturación masiva.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {/* Cron */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Facturación automática</span>
            {cron.enabled
              ? <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">Activa</Badge>
              : <Badge variant="outline">Apagada</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Ejecutar facturación automática</Label>
              <p className="text-xs text-muted-foreground">Si está apagada, solo se factura desde el módulo manual.</p>
            </div>
            <Switch checked={cron.enabled} onCheckedChange={v => setCron({ ...cron, enabled: v })} />
          </div>

          <div>
            <Label>Modo de programación</Label>
            <Select
              value={cron.schedule_mode}
              onValueChange={(v) => setCron({ ...cron, schedule_mode: v as typeof cron.schedule_mode })}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first_business_day">Primer día hábil del mes (recomendado)</SelectItem>
                <SelectItem value="fixed_day">Día fijo del mes</SelectItem>
                <SelectItem value="manual">Manual (sin auto-ejecución)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {cron.schedule_mode === 'first_business_day' && (
                <>Se genera la corrida automática el <strong>primer lunes a viernes</strong> de cada mes a la hora local indicada.
                  Si el día 1 cae sábado o domingo, se ejecuta el lunes siguiente.</>
              )}
              {cron.schedule_mode === 'fixed_day' && (
                <>Se genera la corrida automática el <strong>día exacto</strong> del mes indicado abajo, sin importar si es fin de semana o feriado.</>
              )}
              {cron.schedule_mode === 'manual' && (
                <>El cron no ejecutará nada automáticamente. Tú generas las facturas desde
                  <strong> Facturación masiva</strong> con el botón <em>Generar lote</em> y/o desde
                  acciones <strong>en lote</strong> sobre contratos seleccionados.</>
              )}
            </p>
          </div>

          {cron.schedule_mode !== 'manual' && (
            <div className="grid grid-cols-2 gap-3">
              {cron.schedule_mode === 'fixed_day' && (
                <div>
                  <Label>Día del mes</Label>
                  <Input type="number" min={1} max={28} value={cron.day_of_month}
                    onChange={e => setCron({ ...cron, day_of_month: Math.max(1, Math.min(28, Number(e.target.value))) })} />
                  <p className="text-xs text-muted-foreground mt-1">1–28 (evita meses cortos)</p>
                </div>
              )}
              <div className={cron.schedule_mode === 'first_business_day' ? 'col-span-2' : ''}>
                <Label>Hora (24h, hora local GT)</Label>
                <Input type="number" min={0} max={23} value={cron.hour_local}
                  onChange={e => setCron({ ...cron, hour_local: Math.max(0, Math.min(23, Number(e.target.value))) })} />
                <p className="text-xs text-muted-foreground mt-1">Zona horaria: {cron.timezone}</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label>Notificar admins al ejecutar</Label>
              <p className="text-xs text-muted-foreground">Envía push al finalizar la corrida automática.</p>
            </div>
            <Switch checked={cron.notify_admins} onCheckedChange={v => setCron({ ...cron, notify_admins: v })} />
          </div>
        </CardContent>
      </Card>

      {/* Approval + NIT block */}
      <Card>
        <CardHeader><CardTitle>Reglas de emisión</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Requerir aprobación antes de emitir FEL</Label>
              <p className="text-xs text-muted-foreground">
                Las facturas se crean como <strong>draft</strong> y un admin las aprueba.
                Quedan registrados el usuario y la fecha de aprobación.
              </p>
            </div>
            <Switch checked={flags.require_approval} onCheckedChange={v => setFlags({ ...flags, require_approval: v })} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Bloquear emisión si el NIT no está validado</Label>
              <p className="text-xs text-muted-foreground">
                Evita pagar certificaciones rechazadas. El NIT se valida desde la ficha de la cuenta
                (botón <em>Validar NIT</em>). Consumidor Final (CF) siempre se permite.
              </p>
            </div>
            <Switch checked={flags.block_unvalidated_nit} onCheckedChange={v => setFlags({ ...flags, block_unvalidated_nit: v })} />
          </div>
        </CardContent>
      </Card>

      {/* Suspension policy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="w-5 h-5" /> Política de suspensión automática
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Una cuenta se suspende cuando cumple <strong>cualquiera</strong> de las dos condiciones siguientes.
            Al suspenderse, se desactivan cámaras, paneles y patrullaje hasta que el adeudo quede pagado.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Facturas vencidas para suspender</Label>
              <Input
                type="number"
                min={1}
                max={12}
                value={policy.overdue_count}
                onChange={(e) => setPolicy((p) => ({ ...p, overdue_count: Math.max(1, Math.min(12, Number(e.target.value))) }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Suspender cuando el cliente tenga <strong>≥ N facturas</strong> en estado vencido.
                <br />Ej: 2 = suspende a la segunda factura vencida.
              </p>
            </div>
            <div>
              <Label>Días de mora para suspender</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={policy.overdue_days}
                onChange={(e) => setPolicy((p) => ({ ...p, overdue_days: Math.max(1, Math.min(365, Number(e.target.value))) }))}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Suspender si la factura más antigua lleva <strong>≥ X días</strong> vencida,
                aunque solo haya una.
                <br />Ej: 60 = suspende después de 2 meses sin pagar.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PCI / no card storage notice */}
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <AlertTriangle className="w-5 h-5" /> Política de medios de pago
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>
            <strong>ASI Security no almacena datos de tarjetas de crédito o débito.</strong>{' '}
            Por cumplimiento PCI-DSS y prevención de riesgo legal, no existe cobro automático recurrente
            con tarjeta.
          </p>
          <p className="text-muted-foreground">
            El cliente debe ingresar manualmente su tarjeta en cada pago, ya sea desde la app móvil o desde
            el dashboard. La pasarela (QPay Pro) procesa la transacción y nosotros sólo recibimos
            confirmación de pago — nunca el número de tarjeta, CVV ni fecha de expiración.
          </p>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {savedAt && <span className="text-xs text-muted-foreground">Guardado {savedAt.toLocaleTimeString()}</span>}
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
